// @flow

import * as React from 'react';
import { NativeBridge } from '@ta/common.webview';
import { Authenticate, Route, Visitor } from '@ta/lithium.context';
import type { LoginFunction, LoginResponse } from '@ta/login.types';

/**
 * This hook is used in LithiumBridge as part of Authenticate context. Other contexts are not available yet, therefore passing them via parameters.
 * @returns {Function} login
 */
const useAuthGated = (): LoginFunction => {
  const { userId } = React.useContext(Visitor);
  const [route] = React.useContext(Route);
  const authenticate = React.useContext(Authenticate);
  const geoId = route.params.geoId || -1;

  const authGate = React.useCallback(
    props => {
      if (userId && !props.forceLogin) {
        return Promise.resolve({
          userId,
          hasCreatedMember: null,
        });
      }

      return new Promise(resolve => {
        if (NativeBridge.isNativeWebview()) {
          NativeBridge.showRegistration(false);
        } else {
          // Gotta log in first
          const regOptions = {
            flow: props.flow || 'CORE_COMBINED',
            pid: props.pid || -1,
            userRequestedForce: true,
            extraQueryParams: Object.assign(
              { flowOrigin: props.flowOrigin || 'join' },
              props.extraQueryParams,
            ),
            displayOnShowOverlay: props.deferShowOverlay || false,
            locationId: geoId,
            onSuccess: regData => {
              const response: LoginResponse = {
                // eslint-disable-next-line camelcase
                userId: regData?.user_id ? `${regData.user_id}` : null,
                hasCreatedMember: !!regData?.hasCreatedMember,
              };
              if (props.onSuccess) {
                props.onSuccess(response);
              }
              authenticate({ userId: response.userId });
              resolve(response);
            },
            onClose: props.onClose,
            closeX: props.closeX !== false,
            closeOnDocClick: props.closeOnDocClick !== false,
            windowShadeOption: props.windowShadeOption || 'dark',
          };

          global.require(['trjs!ta/registration/RegOverlay'], RegOverlay => {
            if (props.onLogin) {
              props.onLogin();
            }
            RegOverlay.showRegOverlayByPageModel(
              null,
              null,
              // eslint-disable-next-line no-restricted-globals
              window.require('page-model'),
              regOptions,
            );
          });
        }
      });
    },
    [userId, geoId, authenticate],
  );

  return authGate;
};

export default useAuthGated;
